import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { PageLoader } from '../../../shared/ui/Spinner'
import { ErrorMessage } from '../../../shared/ui/ErrorMessage'
import {
  getQuotesOverview,
  type QuotesSummaryPosition,
  type QuoteSummaryItem,
} from '../../../shared/api/quotesSelection.api'

/**
 * Read-only сводка всех квот заявки для закупщика (с 22.05.2026).
 * Аналог менеджерской `/quotes-selection`, но без выбора и сохранения.
 *
 * Что показывает (фильтр на бэке см. quotes-overview.service.ts):
 *  - квоты только закупочных матриц (Китай / Производители),
 *  - без синтетических «Цена менеджера»,
 *  - закупщик видит и свои, и чужие квоты, и квоты-заготовки без цены, и
 *    нулевые квоты («позиция отсутствует»).
 *  - бейдж «min» — среди видимых квот, только по цене > 0.
 */
export function QuotesOverviewPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data, isLoading, error } = useQuery({
    queryKey: ['quotes-overview', id],
    queryFn: () => getQuotesOverview(id!),
    enabled: !!id,
  })

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const toggleCollapsed = (posId: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(posId)) next.delete(posId)
      else next.add(posId)
      return next
    })
  }

  if (isLoading) return <PageLoader />
  if (error) return <ErrorMessage error={error} />
  if (!data) return null

  const allCollapsed = collapsed.size >= data.positions.length && data.positions.length > 0
  const handleCollapseAll = () => setCollapsed(new Set(data.positions.map((p) => p.id)))
  const handleExpandAll = () => setCollapsed(new Set())

  return (
    <div className="space-y-4 pb-10">
      {/* Шапка — закупщики НЕ видят имя заказчика, только код клиента и
         номер заявки. Оба кликабельные: код → карточка клиента, номер →
         сама заявка. Правило Сергея от 23.05.2026. */}
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 px-5 py-3 flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => navigate(`/requests/${id}`)}
          className="text-sm text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100"
        >
          ← К заявке
        </button>
        <span className="text-gray-300 dark:text-gray-600">·</span>
        {/* Код клиента — НЕ ссылка: закупщик не должен попадать в карточку
           клиента (правило Сергея от 23.05.2026). */}
        <span className="font-mono text-sm font-semibold text-gray-900 dark:text-gray-100">
          {data.request.clientCode ?? '—'}
        </span>
        <span className="text-gray-300 dark:text-gray-600">·</span>
        <Link
          to={`/requests/${id}`}
          className="font-mono text-sm font-semibold text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 hover:underline"
          title="Карточка заявки"
        >
          {data.request.requestNumber ?? '—'}
        </Link>
        <span className="flex-1" />
        <span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
          Только просмотр · закупка
        </span>
      </div>

      {/* Свод */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryCard label="Всего позиций" value={data.summary.total} />
        <SummaryCard
          label="С квотами"
          value={data.summary.total - data.summary.noQuotes}
          tone="emerald"
        />
        <SummaryCard label="Без квот" value={data.summary.noQuotes} tone="amber" />
        <SummaryCard label="Матрицы" value="Китай · Производители" small />
      </div>

      <div className="flex items-center gap-2 text-xs">
        <button
          type="button"
          onClick={allCollapsed ? handleExpandAll : handleCollapseAll}
          className="px-2 py-1 rounded border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50"
        >
          {allCollapsed ? 'Раскрыть все' : 'Свернуть все'}
        </button>
        <span className="text-gray-400">
          {data.positions.length} позиций · {countAllQuotes(data.positions)} квот
        </span>
      </div>

      {/* Список позиций */}
      <div className="space-y-3">
        {data.positions.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-200 dark:border-gray-700 p-8 text-center text-sm text-gray-500 dark:text-gray-400">
            В этой заявке нет активных закупочных позиций.
          </div>
        ) : (
          data.positions.map((p) => (
            <ReadOnlyPositionCard
              key={p.id}
              position={p}
              collapsed={collapsed.has(p.id)}
              onToggleCollapsed={() => toggleCollapsed(p.id)}
            />
          ))
        )}
      </div>
    </div>
  )
}

function countAllQuotes(positions: QuotesSummaryPosition[]): number {
  return positions.reduce((acc, p) => acc + p.quotes.length, 0)
}

function SummaryCard({
  label,
  value,
  tone,
  small,
}: {
  label: string
  value: number | string
  tone?: 'emerald' | 'amber'
  small?: boolean
}) {
  const valueCls =
    tone === 'emerald'
      ? 'text-emerald-700 dark:text-emerald-300'
      : tone === 'amber'
        ? 'text-amber-700 dark:text-amber-300'
        : 'text-gray-900 dark:text-gray-100'
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2">
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className={`mt-0.5 font-semibold ${small ? 'text-sm' : 'text-lg'} ${valueCls}`}>
        {value}
      </div>
    </div>
  )
}

const MATRIX_BY_SOURCE: Record<string, string> = {
  STOCK: 'Склад',
  CHINA: 'Китай',
  RF: 'РФ',
  MANUFACTURER: 'Производители',
  OTHER: 'Стоки',
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(
    d.getFullYear() % 100,
  ).padStart(2, '0')}`
}

function ReadOnlyPositionCard({
  position,
  collapsed,
  onToggleCollapsed,
}: {
  position: QuotesSummaryPosition
  collapsed: boolean
  onToggleCollapsed: () => void
}) {
  const cardTone =
    position.quotes.length === 0 ? 'border-l-gray-300 dark:border-l-gray-600' : 'border-l-blue-500'

  return (
    <div
      className={`bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 border-l-4 ${cardTone}`}
    >
      <button
        type="button"
        onClick={onToggleCollapsed}
        className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <span className="text-xs text-gray-400 dark:text-gray-500 w-6 shrink-0">
          {position.index}
        </span>
        <Link
          to={`/request-items/${position.id}`}
          onClick={(e) => e.stopPropagation()}
          className="font-mono font-semibold text-sm text-gray-900 dark:text-gray-100 hover:text-brand-600 dark:hover:text-brand-400 truncate"
        >
          {position.partNumber}
        </Link>
        <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
          · {position.brand ?? '—'} · {position.quantity} шт
        </span>
        <span className="flex-1" />
        <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">
          {position.quotes.length} квот
        </span>
        <span className="text-gray-400 dark:text-gray-500 text-sm w-4">
          {collapsed ? '▸' : '▾'}
        </span>
      </button>

      {!collapsed && (
        <div className="px-2 pb-3">
          {position.quotes.length === 0 ? (
            <div className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400 text-center">
              По этой позиции пока нет закупочных квот.
            </div>
          ) : (
            <>
              {/* Десктоп */}
              <div className="overflow-x-auto hidden md:block">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                      <th className="px-3 py-2 text-left w-16"></th>
                      <th className="px-3 py-2 text-left">Матрица</th>
                      <th className="px-3 py-2 text-left">Поставщик</th>
                      <th className="px-3 py-2 text-left">Закупщик</th>
                      <th className="px-3 py-2 text-left">Дата</th>
                      <th className="px-3 py-2 text-right">Цена</th>
                      <th className="px-3 py-2 text-left">Заметка</th>
                    </tr>
                  </thead>
                  <tbody>
                    {position.quotes.map((q) => (
                      <QuoteRowReadOnly key={q.id} quote={q} />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Мобильный */}
              <div className="md:hidden space-y-2 px-2">
                {position.quotes.map((q) => (
                  <QuoteCardMobile key={q.id} quote={q} />
                ))}
              </div>
            </>
          )}

          <div className="px-4 pt-3 text-xs text-gray-500 dark:text-gray-400">
            <Link
              to={`/request-items/${position.id}`}
              className="text-brand-600 dark:text-brand-400 hover:underline"
            >
              Перейти к позиции
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}

function QuoteRowReadOnly({ quote }: { quote: QuoteSummaryItem }) {
  const hasPrice = quote.price != null
  const isZero = quote.price === 0
  return (
    <tr className="border-b border-gray-50 dark:border-gray-800 last:border-b-0">
      <td className="px-3 py-2 align-top">
        {quote.isMin && (
          <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
            min
          </span>
        )}
        {!hasPrice && (
          <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
            ждём
          </span>
        )}
        {isZero && (
          <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300">
            нет позиции
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
        {MATRIX_BY_SOURCE[quote.sourceType] ?? quote.sourceType}
      </td>
      <td className="px-3 py-2 text-gray-900 dark:text-gray-100 truncate">{quote.supplierName}</td>
      <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{quote.procurementUserName}</td>
      <td className="px-3 py-2 text-gray-600 dark:text-gray-400 font-mono text-xs">
        {formatDate(quote.createdAt)}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {hasPrice ? (
          <>
            <span className="text-gray-900 dark:text-gray-100">
              {quote.price!.toFixed(2)} {quote.currency}
            </span>
            {quote.currency !== 'CNY' && quote.priceCny != null && (
              <span className="ml-1.5 text-xs text-gray-500">≈ {quote.priceCny.toFixed(2)} ¥</span>
            )}
          </>
        ) : (
          <span className="text-xs italic text-gray-400">нет цены</span>
        )}
      </td>
      {/* Заметка показывается полностью — без truncate. Длинные комментарии
         поставщика менеджеру в КП важны целиком (правило 23.05.2026). */}
      <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 whitespace-pre-wrap break-words align-top">
        {quote.note ?? '—'}
      </td>
    </tr>
  )
}

function QuoteCardMobile({ quote }: { quote: QuoteSummaryItem }) {
  const hasPrice = quote.price != null
  return (
    <div className="p-2 rounded border border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-2">
        <span className="font-medium text-sm truncate">{quote.supplierName}</span>
        {quote.isMin && (
          <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
            min
          </span>
        )}
        {!hasPrice && (
          <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
            ждём
          </span>
        )}
      </div>
      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
        {MATRIX_BY_SOURCE[quote.sourceType] ?? quote.sourceType} · {quote.procurementUserName} ·{' '}
        <span className="font-mono">{formatDate(quote.createdAt)}</span>
      </div>
      <div className="text-sm font-medium mt-0.5">
        {hasPrice ? (
          <>
            {quote.price!.toFixed(2)} {quote.currency}
            {quote.currency !== 'CNY' && quote.priceCny != null && (
              <span className="ml-1.5 text-xs text-gray-500">≈ {quote.priceCny.toFixed(2)} ¥</span>
            )}
          </>
        ) : (
          <span className="text-xs italic text-gray-400">нет цены</span>
        )}
      </div>
      {quote.note && (
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 whitespace-pre-wrap break-words">
          {quote.note}
        </div>
      )}
    </div>
  )
}
